import React, { useState, useEffect, useRef } from 'react';
import HCaptcha from '@hcaptcha/react-hcaptcha';

import FooterHome from './FooterHome';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationCircle } from '@fortawesome/free-solid-svg-icons';
import { faGift } from '@fortawesome/free-solid-svg-icons';
import { isValidEmail } from '../utils/utils';
import {
  companySizes,
  companySizeValue,
  errorOnSubscriptionMessages,
} from '../constants/NewsletterSubscriptions';
import { MailerAPI } from '../utils/mailer-api';

const translations = {
  es: {
    headerTitle: 'Crear una Cuenta',
    headerSubtitle:
      '¿Quieres unirte a nuestra app?<br />Por ahora es solo con invitación, pero puedes entrar en la lista de espera aquí.<br />¡Anímate!',
    name: 'Nombre',
    lastName: 'Apellido',
    email: 'Correo Electrónico',
    companySize: 'Tamaño de la empresa',
    submit: 'Registrarse',
    registrationSuccess: 'Registro completado!',
    selectOption: 'Seleccione una opción',
    privacyConsent: 'Acepto la Declaración de privacidad de Bether.',
    contactConsent:
      'Quiero que Bether me contacte y pueda enviarme información comercial.',
  },
  en: {
    headerTitle: 'Create an Account',
    headerSubtitle:
      'Would you like to join our app?<br />For now, it is by invitation only, but you can join the waiting list here.<br />Give it a try!',
    name: 'First Name',
    lastName: 'Last Name',
    email: 'Email Address',
    companySize: 'Company Size',
    submit: 'Register',
    registrationSuccess: 'Registration successful!',
    selectOption: 'Select an option',
    privacyConsent: "I agree to Bether's Privacy Statement.",
    contactConsent:
      'I want Bether to contact me and send me commercial information.',
  },
};

const styles = {
  container: {
    fontFamily: "'Helvetica Neue', Arial, sans-serif",
    fontWeight: '400', // regular por defecto
    margin: 0,
    padding: 0,
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
  },
  mainSection: {
    padding: '50px 20px',
    textAlign: 'center',
    backgroundColor: '#f9f9f9',
    flex: '1',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    boxSizing: 'border-box',
  },
  formContainer: {
    display: 'flex',
    maxWidth: '800px',
    width: '100%',
    borderRadius: '8px',
    overflow: 'hidden',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    backgroundColor: '#fff',
    boxSizing: 'border-box',
    margin: '0 auto',
    // La dirección del flex se asignará de forma dinámica según el dispositivo
  },
  leftColumn: {
    backgroundColor: '#FF2300',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '40px',
    color: '#ffffff',
    textAlign: 'center',
    boxSizing: 'border-box',
  },
  logo: {
    maxWidth: '100%',
    height: 'auto',
    marginBottom: '20px',
  },
  icon: {
    fontSize: '40px',
    marginBottom: '20px',
  },
  infoText: {
    fontWeight: '700', // bold
    fontSize: '14px',
    marginBottom: '10px',
    lineHeight: '1.5',
    textAlign: 'center',
  },
  rightColumn: {
    flex: 1,
    backgroundColor: '#ffffff',
    padding: '40px',
    boxSizing: 'border-box',
  },
  formGroup: {
    marginBottom: '15px',
    textAlign: 'left',
  },
  label: {
    display: 'block',
    marginBottom: '5px',
    color: '#333',
    fontWeight: '700', // cambiado de 600 a 700 para que sea bold
  },
  input: {
    width: '100%',
    padding: '10px 15px',
    borderRadius: '4px',
    border: '1px solid #ccc',
    fontSize: '16px',
    fontFamily: "'Helvetica Neue', Arial, sans-serif",
    fontWeight: '400', // regular
    outline: 'none',
    transition: 'border-color 0.3s',
    boxSizing: 'border-box',
  },
  button: {
    width: '100%',
    padding: '12px',
    backgroundColor: '#ff2300',
    color: '#ffffff',
    border: 'none',
    borderRadius: '4px',
    fontSize: '16px',
    fontWeight: '700', // bold
    cursor: 'pointer',
    transition: 'background-color 0.3s',
    boxSizing: 'border-box',
  },
  buttonDisabled: {
    backgroundColor: '#ccc',
    cursor: 'not-allowed',
  },
  successMessage: {
    color: '#52c41a',
    marginBottom: '15px',
    textAlign: 'left',
  },
  additionalText: {
    marginTop: '15px',
    textAlign: 'center',
    color: '#333',
  },
  link: {
    color: '#ff2300',
    fontWeight: '700', // bold
    textDecoration: 'none',
  },
};

const mailerAPI = new MailerAPI();

function RegistrationPage() {
  const [language, setLanguage] = useState(
    () => localStorage.getItem('language') || 'es'
  );
  const t = translations[language];

  useEffect(() => {
    localStorage.setItem('language', language);
  }, [language]);

  const [loading, setLoading] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [companySize, setCompanySize] = useState('');
  const [success, setSuccess] = useState('');
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);
  const [acceptedContact, setAcceptedContact] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const captchaRef = useRef(null);
  const [emailError, setEmailError] = useState('');

  // Hook para detectar si es pantalla pequeña
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    document.body.style.margin = '0';
    document.body.style.padding = '0';
    document.body.style.overflowX = 'hidden';

    const style = document.createElement('style');
    style.innerHTML = `
      *, *::before, *::after {
        box-sizing: border-box;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.body.style.margin = '';
      document.body.style.padding = '';
      document.body.style.overflowX = '';

      document.head.removeChild(style);
    };
  }, []);

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);

    if (!isValidEmail(value)) {
      setEmailError(language === 'es' ? 'Correo inválido' : 'Invalid email');
    } else {
      setEmailError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const showAlert = (messageEs, messageEn) => {
      alert(language === 'es' ? messageEs : messageEn);
    };

    if (!firstName || !lastName || !email || !companySize) {
      setSuccess('');
      showAlert(
        'Por favor, completa todos los campos.',
        'Please fill out all fields.'
      );
      return;
    }

    if (!isValidEmail(email)) {
      setSuccess('');
      showAlert(
        'Por favor, ingresa un correo válido.',
        'Please enter a valid email.'
      );
      return;
    }

    if (!acceptedPrivacy || !acceptedContact) {
      setSuccess('');
      showAlert(
        'Por favor, acepta todas las condiciones.',
        'Please accept all conditions.'
      );
      return;
    }

    if (!captchaToken) {
      setSuccess('');
      showAlert(
        'Por favor, verifica que eres humano',
        'Please verify that you are human.'
      );
      return;
    }

    const payload = {
      captchaToken,
      firstName,
      lastName,
      email,
      companySize: companySizeValue[companySize],
      language,
    };

    try {
      setLoading(true);
      await mailerAPI.appNewsletterSubscribe(payload);

      setSuccess(t.registrationSuccess);

      setFirstName('');
      setLastName('');
      setEmail('');
      setCompanySize('');
      setAcceptedPrivacy(false);
      setAcceptedContact(false);
      setCaptchaToken(null);
    } catch (error) {
      setSuccess('');
      const { status } = error;
      const [messageEs, messageEn] =
        errorOnSubscriptionMessages[status] ||
        errorOnSubscriptionMessages.default;
      showAlert(messageEs, messageEn);
    } finally {
      setLoading(false);
      resetCaptchaVerification();
    }
  };

  const resetCaptchaVerification = () => {
    setCaptchaToken(null);
    if (captchaRef.current) {
      captchaRef.current.resetCaptcha();
    }
  };

  return (
    <div style={styles.container}>
      <script src="https://js.hcaptcha.com/1/api.js" async defer></script>

      <style>{`
        .spinner-container {
          display: flex;
          justify-content: center;
          align-items: center;
        }
        .spinner {
          width: 16px;
          height: 16px;
          border: 3px solid #f3f3f3;
          border-top: 3px solid #ff2300;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin-right: 8px;
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>

      <main style={styles.mainSection}>
        <div
          style={{
            ...styles.formContainer,
            flexDirection: isMobile ? 'column' : 'row',
          }}
          className="formContainer"
        >
          <div
            style={{
              ...styles.leftColumn,
              width: isMobile ? '100%' : undefined,
              padding: isMobile ? '20px' : styles.leftColumn.padding,
              marginBottom: isMobile ? '20px' : undefined,
            }}
            className="leftColumn"
          >
            <a
              href="/"
              style={
                isMobile
                  ? { display: 'block', width: '100%', textAlign: 'center' }
                  : { display: 'inline-block' }
              }
            >
              <img
                src="/BetherLogowhite.png"
                alt="Company logo"
                style={{
                  ...styles.logo,
                  maxWidth: isMobile ? '100%' : styles.logo.maxWidth,
                  marginBottom: isMobile ? '20px' : styles.logo.marginBottom,
                }}
                className="logo"
              />
            </a>
            <FontAwesomeIcon
              icon={faGift}
              style={{
                ...styles.icon,
                fontSize: isMobile ? '30px' : styles.icon.fontSize,
                marginBottom: isMobile ? '10px' : styles.icon.marginBottom,
              }}
              className="icon"
            />
            <p
              style={{
                ...styles.infoText,
                fontSize: isMobile ? '16px' : styles.infoText.fontSize,
              }}
              className="infoText"
              dangerouslySetInnerHTML={{ __html: t.headerSubtitle }}
            />
          </div>

          <div
            style={{
              ...styles.rightColumn,
              width: isMobile ? '100%' : undefined,
              padding: isMobile ? '20px' : styles.rightColumn.padding,
            }}
            className="rightColumn"
          >
            {success && <p style={styles.successMessage}>{success}</p>}

            <form onSubmit={handleSubmit}>
              <div style={styles.formGroup} className="formGroup">
                <label htmlFor="firstName" style={styles.label}>
                  {t.name}
                </label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  style={styles.input}
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  required
                />
              </div>

              <div style={styles.formGroup} className="formGroup">
                <label htmlFor="lastName" style={styles.label}>
                  {t.lastName}
                </label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  style={styles.input}
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  required
                />
              </div>

              <div
                style={{ ...styles.formGroup, position: 'relative' }}
                className="formGroup"
              >
                <label htmlFor="email" style={styles.label}>
                  {t.email}
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    style={{ ...styles.input, paddingRight: '35px' }}
                    value={email}
                    onChange={handleEmailChange}
                    required
                  />
                  {emailError && (
                    <FontAwesomeIcon
                      icon={faExclamationCircle}
                      style={{
                        position: 'absolute',
                        right: '10px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: 'red',
                        cursor: 'pointer',
                      }}
                      title={emailError}
                    />
                  )}
                </div>
              </div>

              <div style={styles.formGroup} className="formGroup">
                <label htmlFor="companySize" style={styles.label}>
                  {t.companySize}
                </label>
                <select
                  id="companySize"
                  name="companySize"
                  style={styles.input}
                  value={companySize}
                  onChange={(e) => setCompanySize(e.target.value)}
                  required
                >
                  <option value="" disabled>
                    {t.selectOption || 'Seleccione una opción'}
                  </option>
                  {companySizes[language].map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
              </div>

              <div style={styles.formGroup} className="formGroup">
                <div>
                  <HCaptcha
                    sitekey="48ea562e-283e-49d0-87c8-0afeb0f0a860"
                    onVerify={setCaptchaToken}
                    onError={resetCaptchaVerification}
                    ref={captchaRef}
                  />
                </div>
              </div>

              <button
                type="submit"
                style={{
                  ...styles.button,
                  ...(loading ? styles.buttonDisabled : {}),
                }}
                disabled={loading}
              >
                {loading ? (
                  <div className="spinner-container">
                    <span className="spinner"></span> Procesando...
                  </div>
                ) : (
                  t.submit
                )}
              </button>

              <div
                style={{
                  ...styles.formGroup,
                  marginTop: '20px',
                  marginLeft: '15px',
                }}
                className="formGroup"
              >
                <input
                  type="checkbox"
                  id="privacyConsent"
                  name="privacyConsent"
                  checked={acceptedPrivacy}
                  onChange={(e) => setAcceptedPrivacy(e.target.checked)}
                  required
                />
                <label
                  htmlFor="privacyConsent"
                  style={{ marginLeft: '5px', fontSize: '14px' }}
                >
                  {language === 'es' ? (
                    <>
                      Acepto la{' '}
                      <a
                        href="/legal"
                        target="_blank"
                        style={{
                          color: '#ff2300',
                          textDecoration: 'underline',
                        }}
                      >
                        Declaración de privacidad
                      </a>{' '}
                      de Bether.
                    </>
                  ) : (
                    t.privacyConsent
                  )}
                </label>
              </div>

              <div
                style={{ ...styles.formGroup, marginLeft: '15px' }}
                className="formGroup"
              >
                <input
                  type="checkbox"
                  id="contactConsent"
                  name="contactConsent"
                  checked={acceptedContact}
                  onChange={(e) => setAcceptedContact(e.target.checked)}
                  required
                />
                <label
                  htmlFor="contactConsent"
                  style={{ marginLeft: '5px', fontSize: '14px' }}
                >
                  {t.contactConsent}
                </label>
              </div>
            </form>
          </div>
        </div>
      </main>

      <FooterHome language={language} setLanguage={setLanguage} />
    </div>
  );
}

export default RegistrationPage;
